import { Component, OnInit, Inject, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, TableIcon, formatCurrencyDisplay, formatCPriceDisplay } from '@interfaces';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { numeric, costValue, priceDisplay, priceHistory, summarizeCosts, usesDifferential } from '../pricing-summary';
import { SkuPriceUpdateComponent } from '../sku-price-update/sku-price-update.component';
@Component({selector:'app-inventory-list', imports:[RmsModule, SkuPriceUpdateComponent], templateUrl:'./inventory-list.component.html', styleUrls:['./inventory-list.component.scss']})
export class InventoryListComponent implements OnInit, AfterViewInit, OnDestroy {
  InventoryList: any[] = [];
  sections: any[] = [];
  editorSKUs:any[]=[];
  pricingOpen=false;
  @ViewChild('pinnedControls') pinnedControls!:ElementRef<HTMLElement>;
  @ViewChild('inventoryRoot') inventoryRoot!:ElementRef<HTMLElement>;
  private controlsObserver?:ResizeObserver;
  ngAfterViewInit() {
    this.controlsObserver=new ResizeObserver(()=>this.inventoryRoot.nativeElement.style.setProperty('--controls-height', this.pinnedControls.nativeElement.offsetHeight+'px'));
    this.controlsObserver.observe(this.pinnedControls.nativeElement);
  }
  ngOnDestroy() { this.controlsObserver?.disconnect(); }
  closePricing() { this.pricingOpen=false; this.editorSKUs=[]; }
  removeSKUs(ids:number[]) {
    this.InventoryList.filter(r=>ids.includes(r.InventoryTypeID)).forEach(r=>r.Selected=false);
    this.openPricing();
  }
  referenceSelectionChanged() { if(this.pricingOpen) this.openPricing(); }
  get selectedRows() { return this.InventoryList.filter(r=>r.Selected && !r.PricingExcluded && this.inView(r)); }
  private inView(row:any) { return this.data.section==='spot' ? (numeric(row.AvailableQty)??0)>0 : this.data.section==='forward' ? (numeric(row.ForwardQty)??0)>0 : (numeric(row.AvailableQty)??0)+(numeric(row.ForwardQty)??0)>0; }
  get allSelected() { const rows=this.InventoryList.filter(r=>!r.PricingExcluded && this.inView(r)); return rows.length>0 && rows.every(r=>r.Selected); }
  get someSelected() { return this.selectedRows.length>0 && !this.allSelected; }
  selectAll(checked:boolean) { this.InventoryList.filter(r=>!r.PricingExcluded && this.inView(r)).forEach(r=>r.Selected=checked); this.referenceSelectionChanged(); }
  openPricing() {
    this.pricingOpen=true;
    const rows=this.selectedRows;
    this.editorSKUs=(this.data.skus || []).filter((s:any)=>rows.some(r=>r.InventoryTypeID===s.InventoryTypeID)).map((s:any)=>{
      const inventory=rows.filter(r=>r.InventoryTypeID===s.InventoryTypeID);
      const available=this.InventoryList.filter(r=>r.InventoryTypeID===s.InventoryTypeID && !r.PricingExcluded);
      const spot=summarizeCosts(inventory.some(r=>(numeric(r.AvailableQty) ?? 0)>0)?inventory:available,'AvailableQty','aged');
      const forward=summarizeCosts(inventory.some(r=>(numeric(r.ForwardQty) ?? 0)>0)?inventory:available,'ForwardQty','landed');
      return {...s,Inventory:inventory,TotalAvailableQty:spot.bags,TotalForwardQty:forward.bags,SpotCostDetail:spot.detail,ForwardCostDetail:forward.detail};
    });
  }
  pricingSaved(event:any) {
    for(const price of event.prices || []) {
      for(const sku of this.data.skus || []) if(sku.InventoryTypeID===price.InventoryTypeID) {
        sku.LatestSavedPricing={...price};
        sku.AverageHistoricalReplacementFixedDisplay=formatCurrencyDisplay(price.ReplacementFixed);
        sku.AverageHistoricalReplacementDiffDisplay=priceDisplay(numeric(price.ReplacementDiff),null);
        sku.AverageLatestAskFixedDisplay=formatCurrencyDisplay(price.AskPriceFixed);
        sku.AverageLatestAskDiffDisplay=priceDisplay(numeric(price.AskPriceDiff),null);
      }
      for(const row of this.InventoryList) if(row.InventoryTypeID===price.InventoryTypeID) {row.AskFixed=price.AskPriceFixed;row.AskDiff=priceDisplay(price.AskPriceDiff,null);}
    }
    this.rebuild(); this.ref.sendData({success:true});
  }
  excluded: any[] = [];
  groupByMonth = false;
  sortBy = 'position';
  sortOptions = [{value:'position',label:'Current position'}, {value:'name',label:'Coffee name'}, {value:'bags',label:'Bags available'}, {value:'cost',label:'Aged cost'}];
  inventoryColumns: Column[] = [
    {label:'Selected',key:'Selected',type:'checkbox',edit:true,checkAll:true,width:5},
    {label:'Reference #',key:'InventoryID'}, {label:'Coffee name',key:'InventoryTypeName',width:22},
    {label:'Warehouse',key:'WarehouseName'}, {label:'Current position',key:'FinalPosition'}, {label:'Bags available',key:'SectionQty'},
    {label:'Landed cost',key:'LandedDisplay'}, {label:'Aged cost',key:'AgedDisplay'},
    {label:'Ask fixed',key:'AskFixed',type:'currency'}, {label:'Ask diff',key:'AskDiff'},
    {label:'Futures month',key:'FutureCompositeName'}, {label:'Date in',key:'DateIn',type:'date'}
  ];
  icons: TableIcon[] = [{icon:'close',title:'Exclude reference from averages',action:(row:any)=>this.exclude(row)}];
  restoreIcons: TableIcon[] = [{icon:'undo',title:'Restore reference to averages',action:(row:any)=>this.restore(row)}];
  constructor(private ref:RmsPulldownRef, @Inject(RMS_PULLDOWN_DATA) private data:any) {}
  ngOnInit() {
    this.InventoryList = (Array.isArray(this.data) ? this.data : this.data.inventory || []).map((row:any) => {
      const latest = priceHistory(row.LatestInventoryTypePriceJson)[0] ?? {};
      return {...row, AgedDisplay:priceDisplay(costValue(row,'aged',true),costValue(row,'aged',false)),
        LandedDisplay:priceDisplay(costValue(row,'landed',true),costValue(row,'landed',false)),
        AskFixed:latest.AskPriceFixed ?? 0, AskDiff:priceDisplay(numeric(latest.AskPriceDiff) ?? 0,null)};
    });
    this.rebuild();
  }
  summaryRows(section: any): Record<string, string | number | null>[] {
    const row = (label: string, summary: any) => ({
      InventoryTypeName: label + ' · ' + summary.aged.count + ' refs',
      SectionQty: summary.aged.bags.toLocaleString(undefined, { maximumFractionDigits: 0 }),
      LandedDisplay: summary.landed.display,
      AgedDisplay: summary.aged.display
    });
    return [row('Section total', section)];
  }
  close() { this.ref.close(); }
  exclude(row:any) { const item=this.InventoryList.find(i=>i.InventoryID===row.InventoryID); if(item) item.PricingExcluded=true; this.changed(); }
  restore(row:any) { const item=this.InventoryList.find(i=>i.InventoryID===row.InventoryID); if(item) item.PricingExcluded=false; this.changed(); }
  restoreAll() { this.InventoryList.forEach(r=>r.PricingExcluded=false); this.changed(); }
  private changed() { this.rebuild(); this.ref.sendData({excluded:this.excluded.map(r=>r.InventoryID)}); }
  private positionDate(row:any):number {
    for(const key of ['ETA','VesselETA','ETD','DateIn']) { const t=Date.parse(row[key]); if(Number.isFinite(t)) return t; }
    const month = String(row.FinalPosition ?? '').match(/(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[^0-9]*(\d{2,4})/i);
    return month ? Date.parse(`${month[1]} 1 ${month[2].length===2?'20':''}${month[2]}`) : Number.MAX_SAFE_INTEGER;
  }
  rebuild() {
    this.excluded=this.InventoryList.filter(r=>r.PricingExcluded);
    this.sections=[];
    for(const kind of ['spot','forward'] as const) {
      for(const hedged of [true,false]) {
      const included=this.InventoryList.filter(r=>!r.PricingExcluded && this.inView(r) && usesDifferential(r)===hedged);
        if(this.data.section && this.data.section!=='all' && this.data.section!==kind) continue;
        const quantity=kind==='spot'?'AvailableQty':'ForwardQty';
        const groups=new Map<string,any[]>();
        for(const row of included.filter(r=>(numeric(r[quantity])??0)>0)) {
          const key=this.groupByMonth ? row.FutureCompositeName || 'No futures month' : '';
          const display={...row,SectionQty:numeric(row[quantity])??0};
          Object.defineProperty(display,'Selected',{enumerable:true,get:()=>!!row.Selected,set:(value)=>{row.Selected=value;this.referenceSelectionChanged();}});
          groups.set(key,[...(groups.get(key)||[]),display]);
        }
        for(const [month,items] of groups) {
          items.sort((a,b)=>this.sortBy==='name'?String(a.InventoryTypeName).localeCompare(String(b.InventoryTypeName)):
            this.sortBy==='bags'? b.SectionQty-a.SectionQty:
            this.sortBy==='cost'?(costValue(b,'aged',hedged)??-Infinity)-(costValue(a,'aged',hedged)??-Infinity):this.positionDate(a)-this.positionDate(b));
          const section={title:(hedged?'Hedged ':'Unhedged ')+(kind==='spot'?'Spot':'FWD'),kind,hedged,month,rows:items,aged:summarizeCosts(items,quantity,'aged'),landed:summarizeCosts(items,quantity,'landed')};
          section.title+=(month?' · '+month:'');this.sections.push(section);
        }
      }
    }
  }
}
